import type { TFunction } from 'i18next'
import { useTranslation } from 'react-i18next'

import { getShipmentDispatchTargets } from '#/entities/shipment'
import type { ShipmentDetail } from '#/shared/api/generated/shipment/models'
import { Button } from '#/shared/ui/button'
import { RefreshIcon, SendIcon } from '#/shared/ui/icons'

import { usePostToLoadboard } from '../model/use-post-to-loadboard'

interface PostedActionsProps {
  shipment?: ShipmentDetail
  compact?: boolean
  onDispatch?: () => void
  onRemoved?: () => void
}

function actionLabels(
  t: TFunction,
  compact: boolean | undefined,
  hasMultiple: boolean,
) {
  if (compact) {
    return {
      dispatch: t('detailToolbar.dispatch'),
      upload: t('detailToolbar.repost'),
      remove: t('detailToolbar.deleteFromLB'),
    }
  }
  if (hasMultiple) {
    return {
      dispatch: t('detailToolbar.dispatchViaCd'),
      upload: t('detailToolbar.uploadToLBs'),
      remove: t('detailToolbar.deleteFromLBs'),
    }
  }
  return {
    dispatch: t('detailToolbar.dispatch'),
    upload: t('detailToolbar.uploadToLB'),
    remove: t('detailToolbar.deleteFromLB'),
  }
}

export function PostedActions({
  shipment,
  compact,
  onDispatch,
  onRemoved,
}: PostedActionsProps) {
  const { t } = useTranslation()
  const shipmentId = shipment?.id
  const { repost, remove, isPending, isReposting } = usePostToLoadboard(
    shipmentId,
    { onRemoved },
  )
  const { hasMultiple, loadboardChoice } = getShipmentDispatchTargets(shipment)
  const labels = actionLabels(t, compact, hasMultiple)

  return (
    <div className="flex items-center gap-2.5">
      <Button size="xs" className="px-2.5" onClick={onDispatch}>
        {!compact && <SendIcon className="size-3.5" />}
        {labels.dispatch}
      </Button>
      <Button
        variant="outline-primary"
        size="xs"
        className="px-2.5"
        disabled={!shipmentId || isPending}
        onClick={() => repost(loadboardChoice)}
      >
        <RefreshIcon
          className={`size-3.5${isReposting ? ' animate-spin' : ''}`}
        />
        {labels.upload}
      </Button>
      {!compact && (
        <Button
          variant="outline"
          size="xs"
          className="px-2.5"
          disabled={!shipmentId || isPending}
          onClick={() => remove(loadboardChoice)}
        >
          {labels.remove}
        </Button>
      )}
    </div>
  )
}
